/**
 * Iter 425.j (Dirk, 2026-03-01) — Searchable project dropdown.
 *
 * Full projects list can hit 100+ entries; the plain `<select>` was
 * unusable. This lightweight combobox filters by short_id / title /
 * name as the user types. Closes on outside click and Escape.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Search } from "lucide-react";

export default function ProjectSearchSelect({
  value,
  projects,
  onChange,
  testid,
  allowNone = true,
  noneLabel = "— none —",
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrapRef = useRef(null);

  const selected = (projects || []).find((p) => p.id === value) || null;
  const label = selected
    ? `${selected.short_id ? selected.short_id + " · " : ""}${selected.title || selected.name || selected.id.slice(0, 8)}`
    : noneLabel;

  const filtered = useMemo(() => {
    const list = projects || [];
    const s = q.trim().toLowerCase();
    if (!s) return list.slice(0, 100);
    return list.filter((p) => {
      const hay = `${p.short_id || ""} ${p.title || ""} ${p.name || ""}`.toLowerCase();
      return hay.includes(s);
    }).slice(0, 100);
  }, [projects, q]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="relative" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="hv-input text-xs w-full text-left flex items-center justify-between gap-1"
        data-testid={`${testid}-toggle`}
      >
        <span className={selected ? "" : "text-[#647087]"}>{label}</span>
        <Search className="w-3 h-3 text-[#647087] shrink-0" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full min-w-[240px] max-h-[280px] overflow-hidden rounded border border-white/10 bg-[#0a0b0e] shadow-lg"
             data-testid={`${testid}-panel`}>
          <div className="p-1.5 border-b border-white/10">
            <input
              autoFocus
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search projects…"
              className="hv-input text-xs w-full"
              data-testid={`${testid}-search`}
            />
          </div>
          <div className="max-h-[240px] overflow-y-auto py-1">
            {allowNone && (
              <button
                type="button"
                className={`w-full text-left px-2 py-1 text-xs hover:bg-white/[0.04] ${!value ? "text-emerald-300" : "text-[#a0aabf]"}`}
                onClick={() => { onChange(""); setOpen(false); setQ(""); }}
                data-testid={`${testid}-option-none`}
              >
                {noneLabel}
              </button>
            )}
            {filtered.length === 0 && (
              <div className="px-2 py-2 text-[10px] text-[#647087]">No matches.</div>
            )}
            {filtered.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`w-full text-left px-2 py-1 text-xs hover:bg-white/[0.04] ${p.id === value ? "text-emerald-300" : "text-white"}`}
                onClick={() => { onChange(p.id); setOpen(false); setQ(""); }}
                data-testid={`${testid}-option-${p.id}`}
              >
                {p.short_id ? <span className="text-[#647087] mr-1">{p.short_id}</span> : null}
                {p.title || p.name || p.id.slice(0, 8)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
